import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { MessageCircle, ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Loader from "../loader/Loader";
import ChatUI from "../chatUI/ChatUI";
import { openChat } from "../../redux/chatSlice";
import CareerDetails from "./CareerDetails";
import { HEADING_COLOR } from "../../constants/colors";


const groupInfo = [
  {
    key: "similar",
    title: "Similar Careers",
    description: "Roles that leverage your existing skills with minimal transition effort",
    slice: [0, 3],
  },
  {
    key: "advancement",
    title: "Career Advancement",
    description: "Senior roles that build upon your current experience for career growth",
    slice: [3, 6],
  },
  {
    key: "lateral",
    title: "Lateral Career Moves",
    description: "Different career paths that utilize transferable skills from your background",
    slice: [6],
  },
];

const EmergingAICareers = ({
  careers = [],
  heading = "Careers",
  subtitle = "Career opportunities",
  isLoading = false,
  userId,
  isCareerSaved = () => false,
  onBookmarkChange = () => {},
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expandedCareer, setExpandedCareer] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState({
    similar: true,
    advancement: true,
    lateral: true,
  });

  const groupedCareers = useMemo(() => {
    return groupInfo.reduce((acc, { key, slice }) => {
      acc[key] = slice.length === 1 ? careers.slice(slice[0]) : careers.slice(...slice);
      return acc;
    }, {});
  }, [careers]);


  const getCareerTypeByCareer = (career) => {
    const index = careers.indexOf(career);
    if (index < 3) return "Similar";
    if (index < 6) return "Advancement";
    return "Lateral Move";
  };

  if (isLoading)
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5 mt-5" />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: HEADING_COLOR }}>{heading}</h1>
      </div>
      <p className="text-medium text-gray-500 ml-12 mb-4 mt-1">{subtitle}</p>
      <div className="space-y-4">
        {groupInfo.map(({ key, title, description }) => (
          <div key={key} className="bg-white rounded-lg border border-gray-300">
            <button
              onClick={() => setExpandedGroup((prev) => ({ ...prev, [key]: !prev[key] }))}
              className="w-full text-left p-4 flex items-start justify-between hover:bg-gray-50"
            >
              <div>
                <h2 className="text-lg font-semibold text-purple-700">{title}</h2>
                <p className="text-sm text-gray-500">{description}</p>
              </div>
              {expandedGroup[key] ? (
                <ChevronUp className="w-5 h-5 text-gray-500 mt-1" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500 mt-1" />
              )}
            </button>

            {expandedGroup[key] && (
              <div className="divide-y ">
                {groupedCareers[key]?.length ? (
                  groupedCareers[key].map((career, idx) => {
                    const uniqueId = `${key}-${career._id || idx}`;
                    const isExpanded = expandedCareer === uniqueId;
                    return (
                      <div key={uniqueId} className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {career.name} - {getCareerTypeByCareer(career)}
                            </h3>
                          </div>
                          <button
                            onClick={() => setExpandedCareer(isExpanded ? null : uniqueId)}
                            className="text-purple-600 border border-purple-600 px-2 py-1 rounded-full text-xs font-bold hover:bg-purple-200 flex items-center gap-1 self-start sm:self-auto"
                          >
                            Details {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>

                        <CareerDetails 
                          career={career} 
                          isExpanded={isExpanded} 
                          userId={userId}
                          isSaved={isCareerSaved(career)}
                          onBookmarkChange={onBookmarkChange}
                        />
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-sm text-gray-500">No careers available.</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
        <button
          onClick={() => dispatch(openChat())}
          className="w-12 h-12 md:w-14 md:h-14 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center justify-center"
        >
          <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
        </button>
      </div>

      <ChatUI />
    </div>
  );
};

export default EmergingAICareers;
